import { DomainId } from './data/domains.js';
import { ContentItem } from './data/content.js';
import { Store, StoreProduct } from './data/stores.js';
import { User } from './data/users.js';
import { Account, AccountRelation, PayableAccountRelationType } from './data/accounts.js';
import { Template } from './data/designs.js';
import { Product } from './data/products.js';
import { Shortlink } from './data/shortlinks.js';
import { Email } from './data/messages.js';
import { isSome, NonEmptyList } from './util.js';

export type ResourceHashes = Record<string, string>;

/**
 * This type describes "a finite number in some form". It is intentionally not known which. The only assumptions are:
 *  - Number(Decimal) will return a valid Number.
 *  - String(Decimal) on it will return a string representing a valid number.
 * TypeScript will disallow most operations without converting it to Number or String first. This is a feature.
 */
export type Decimal = number | string;

export type Timestamp = Date | string;
export type Index<T extends { [P in K]: keyof any }, K extends keyof any = 'id'> = Record<T[K], T>;

export type None = undefined | null;
export type Maybe<T> = T | None;
export type Some<T> = NonNullable<T>;

export type ListOr<T> = T | T[];
export type NonEmptyListOr<T> = T | NonEmptyList<T>;
export type AsList<T> = T extends any[] ? T : T[];

export function asList<T> (it: Maybe<ListOr<T>>): AsList<Some<T>>;
export function asList<T> (it: T): AsList<T>;
export function asList<T> (it: T) {
	return (Array.isArray(it) ? it : isSome(it) ? [it] : []);
}

export type Identified = { id: string | number };

/**
 * Type of `id` of an entity. Equivalent to `T['id']`, a bit nicer to write.
 * Any branding is done in the declaration of the `id` itself.
 */
export type Id<T extends Identified> = T['id'];

/**
 * Either an id of an entity, or a whole entity. Useful when you can fetch the entity if needed but can use an existing object too.
 */
export type IdOr<T extends Identified> = T | T['id'];

/**
 * Extracts Id type of IdOr<T>
 */
export type IdOf<T extends Identified | string | number> = T extends (string | number) ? T : T extends Identified ? T['id'] : never;

export interface CommonConfig {
	// empty now, wow.
}

/**
 * Generic interface for user-facing error messages.
 */
export interface UserError {
	/** Machine-readable error description */
	error?: Maybe<string>;
	/** Human-friendly error message */
	message?: Maybe<string>;
	/** Is this a warning or an autocorrected error? */
	continue?: Maybe<boolean>;
}

/**
 * Generic interface for form-related error messages.
 */
export interface FormError extends UserError {
	/** Form field */
	field: string;
}

/**
 * CartItem specific errors.
 */
export interface CartItemError extends UserError {
	/** Item id */
	item: string;
}

export const isCartItemError = (it: UserError): it is CartItemError => typeof (it as any).item == 'string';

export interface DeletionRequest {
	id: string & { __entity?: 'DeletionRequest' };
	created: Timestamp;
	updated: Timestamp;
	completed: Maybe<Timestamp>;
	user: string;
	user_external_account: Maybe<number>;
}

export interface Country {

	id: string & { __entity?: 'Country' };
	name: string;
	
	priority?: number;
	flag?: boolean;
	sovereign?: boolean;
	
	// For countries - is this a part of US
	us?: boolean;
	// For "states" - is this actually not a state
	country?: boolean;
	
}

export interface RosterEntry {
	id: string & { __entity?: 'RosterEntry' };
	size?: Maybe<string>;
	name?: Maybe<string>;
	alias?: Maybe<string>;
	number?: Maybe<string>;
	flag?: Maybe<string>;
	quantity?: Maybe<number>;
}

export interface SavedRoster {
	id: number & { __entity?: 'SavedRoster' };
	user: Maybe<Id<User>>;
	account: Maybe<Id<Account>>;
	name: string;
	players: number;
	edited: Date;
	roster: RosterEntry[];
	deleted: Maybe<Date>;
}

export interface PayoutsSummaryRow {
	date?: string;
	type?: CommissionsType;
	gift_card: Maybe<string>;
	comment: Maybe<string>;
	amount: Decimal;
	earnings: Decimal;
	payouts: Decimal;
	balance?: Decimal;
}

export type CommissionsType = PayableAccountRelationType | 'manual' | 'store' | 'gift_card' | 'purchase';

export interface CommissionsEntry {
	id: number & { __entity?: 'CommissionsEntry' };
	account: Id<Account>;
	store: Maybe<Id<Store>>;
	relation: Maybe<Id<AccountRelation>>;
	order?: number;
	order_item?: number;
	store_product?: Id<StoreProduct>;
	shortlink?: string;
	amount: Decimal;
	source_amount?: number;
	base_amount?: number;
	percent?: number;
	type: CommissionsType;
	comment?: string;
	received: Timestamp;
	gift_card?: string;
}

export interface SalesInfo extends CommissionsEntry {
	name?: string;
	orders?: Decimal;
}

export interface TemplateOwner {
	id: number & { __entity?: 'TemplateOwner' };
	template: Id<Template>;
	account: Id<Account>;
	active: boolean;
}

export interface SessionEvent {
	id: string & { __entity?: 'SessionEvent' };
	domain: Maybe<DomainId>;
	time: Timestamp;
	reported?: Timestamp;
	session: Maybe<string>;
	user: Maybe<Id<User>>;
	account: Maybe<Id<Account>>;
	shortlink: Maybe<Id<Shortlink>>;
	category: Maybe<string>;
	action: string;
	extras?: EventExtras;
	address: Maybe<string>;
	url: Maybe<string>;
	request: Maybe<number>;
	user_agent: Maybe<number>;
}

export interface EventExtras {
	design?: string;
	product?: Id<Product>;
	storeProduct?: string;
	upload?: string;
	savedDesign?: string;
	link?: string;
	eventLabel?: string;
	eventValue?: number | string;
	source?: string;
	referrer?: string;
	order?: number;
	orderItem?: number;
	time?: number;
	ga4?: Maybe<any>;
	email?: Maybe<Id<Email>>;
}

export interface ShippingMessage {
	source: string;
	code: string;
	text: string;
}

export interface RequestLogEntry {
	id: number & { __entity?: 'RequestLogEntry' };
	domain: DomainId;
	started: Timestamp;
	finished?: Timestamp;
	elapsed_ms: number;
	user?: Maybe<Id<User>>;
	account?: Maybe<Id<Account>>;
	session?: string;
	address?: string;
	user_agent?: Maybe<Id<RequestUserAgent>>;
	url?: string;
	method?: string;
	params?: any;
	files?: { path: any[]; name: string; size: number }[];
	code?: number;
	error_message?: string;
	error_stack?: string;
	response?: any;
	breadcrumbs?: {
		time: number;
		tag: string;
		data?: any;
	}[];
}

export interface RequestUserAgent {
	id: number & { __entity?: 'RequestUserAgent' };
	string: string;
	browser: Maybe<string>;
	browser_version: Maybe<string>;
	os: Maybe<string>;
	device_type: Maybe<string>;
	device_vendor: Maybe<string>;
	device_model: Maybe<string>;
}

export interface SizeChart {
	id: number & { __entity?: 'SizeChart' };
	name: string;
	columns: string[];
	values: Record<string, string[]>;
}

export interface UserMailjetData {
	email?: string;
	synced?: string;
	error?: string;
	errorAt?: string;
}
export interface MailjetGlobalState {
	objectsSetup?: string;
	usersList?: string | number;
	lists?: Record<string, string | number>;
	lastSync?: string;
	contactsFetched?: string;
}

export interface FaqItem {
	id: number & { __entity?: 'FaqItem' };
	question: string;
	category: number;
	priority: number;
	answer: ContentItem;
	domains: Maybe<string[]>;
}

export interface FaqCategory {
	id: number & { __entity?: 'FaqCategory' };
	name: string;
	priority: number;
}

